import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Users,
} from 'lucide-react';
import { readScope } from '../utils/scope';
import { useEngagement } from '../hooks/useEngagement';
import { useSession } from '../hooks/useSession';
import {
  deriveStreamMappingProgress,
  type StreamMappingPart,
  type StreamMappingPartStatus,
  type StreamMappingStreamLike,
} from '../utils/streamMappingProgress';
import '../styles/StreamMappingHub.css';

type HubStream = StreamMappingStreamLike & {
  smes?: Array<{ name?: string; contact?: string }>;
};

function decodeParam(value: string | undefined): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function statusLabel(status: StreamMappingPartStatus): string {
  switch (status) {
    case 'done':
      return 'Done';
    case 'in-progress':
      return 'In progress';
    case 'to-do':
    default:
      return 'To do';
  }
}

const StreamMappingHub: React.FC = () => {
  const { streamId } = useParams<{ streamId: string }>();
  const resolvedStreamId = decodeParam(streamId);
  const { loading, currentEngagementId } = useEngagement();
  const streams = readScope().areas as HubStream[];
  const stream = useMemo(
    () => streams.find((candidate) => candidate.id === resolvedStreamId) ?? null,
    [resolvedStreamId, streams],
  );
  const session = useSession(stream?.mappingSessionId, 'discovery');

  if (loading) {
    return (
      <main className="smh-page">
        <p className="smh-muted">Opening stream mapping hub...</p>
      </main>
    );
  }

  if (!currentEngagementId) {
    return (
      <main className="smh-page">
        <EmptyHubState
          label="No engagement selected"
          title="Choose an engagement first"
          body="Stream mapping lives inside an engagement. Pick or create one, then reopen this stream."
          actionLabel="Choose engagement"
          actionTo="/"
        />
      </main>
    );
  }

  if (!stream) {
    return (
      <main className="smh-page">
        <EmptyHubState
          label="Stream not found"
          title="We could not find that stream"
          body="It may have been removed from this engagement, or the link is stale."
          actionLabel="Back to streams"
          actionTo="/streams"
        />
      </main>
    );
  }

  const progress = deriveStreamMappingProgress({ stream, session });
  const smeNames = (stream.smes ?? [])
    .map((sme) => sme.name?.trim())
    .filter((name): name is string => Boolean(name));
  const needsSme = smeNames.length === 0;

  return (
    <main className="smh-page">
      <Link to="/streams" className="smh-back">
        <ArrowLeft size={16} aria-hidden="true" />
        Back to candidate value streams
      </Link>

      <section className="smh-hub" aria-labelledby="stream-mapping-hub-title">
        <header className="smh-hero">
          <div className="smh-identity">
            <span className="smh-eyebrow">Stream mapping hub</span>
            <h1 id="stream-mapping-hub-title">{stream.name}</h1>
            {stream.oneLiner ? <p className="smh-one-liner">{stream.oneLiner}</p> : null}
            <div className="smh-meta" aria-label="Stream readiness">
              {needsSme ? (
                <span className="smh-chip smh-chip--warn">
                  <AlertTriangle size={14} aria-hidden="true" />
                  Assign an SME before mapping can start
                </span>
              ) : (
                smeNames.map((name) => (
                  <span key={name} className="smh-chip">
                    <Users size={14} aria-hidden="true" />
                    SME: {name}
                  </span>
                ))
              )}
            </div>
          </div>
          <div className="smh-count" aria-label="Mapping progress">
            <span>Progress</span>
            <strong>
              {progress.completedCount} of {progress.totalCount}
            </strong>
          </div>
        </header>

        <div className="smh-rail" aria-hidden="true">
          {progress.parts.map((part) => (
            <span
              key={part.id}
              className={`smh-rail-segment smh-rail-segment--${part.status}`}
            />
          ))}
        </div>

        <div className="smh-body">
          <section className="smh-parts" aria-label="Mapping parts">
            {progress.parts.map((part, index) => (
              <PartRow
                key={part.id}
                part={part}
                index={index}
                isNext={progress.nextPart?.id === part.id}
                captureBlocked={needsSme}
              />
            ))}
          </section>

          <UpNext
            needsSme={needsSme}
            smeNames={smeNames}
            nextPart={progress.nextPart}
            completed={progress.completedCount === progress.totalCount}
          />
        </div>
      </section>
    </main>
  );
};

const EmptyHubState: React.FC<{
  label: string;
  title: string;
  body: string;
  actionLabel: string;
  actionTo: string;
}> = ({ label, title, body, actionLabel, actionTo }) => (
  <section className="smh-empty" aria-label={label}>
    <span className="smh-eyebrow">{label}</span>
    <h1>{title}</h1>
    <p>{body}</p>
    <Link to={actionTo} className="smh-review-link">
      {actionLabel}
    </Link>
  </section>
);

const PartRow: React.FC<{
  part: StreamMappingPart;
  index: number;
  isNext: boolean;
  captureBlocked: boolean;
}> = ({ part, index, isNext, captureBlocked }) => {
  const done = part.status === 'done';
  const current = isNext && !captureBlocked;
  return (
    <article
      className={`smh-part smh-part--${part.status}${current ? ' smh-part--current' : ''}`}
      data-testid={`stream-mapping-part-${part.id}`}
      data-status={part.status}
    >
      <span className="smh-part-state" aria-hidden="true">
        {done ? <CheckCircle2 size={18} /> : <span>{index + 1}</span>}
      </span>
      <div className="smh-part-body">
        <h2>
          {part.shortLabel}
          <span className="smh-status">{statusLabel(part.status)}</span>
        </h2>
        <p className="smh-why">{part.why}</p>
        <p className="smh-unlocks">{part.unlocks}</p>
        <p className="smh-summary">{part.summary}</p>
      </div>
      <div className="smh-part-action">
        {done ? (
          <Link
            to={part.target}
            className="smh-review-link"
            aria-label={`Review ${part.shortLabel}`}
          >
            Review
          </Link>
        ) : current ? (
          <Link to={part.target} className="smh-primary" data-testid="stream-hub-primary">
            {part.actionLabel}
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
        ) : (
          <span className="smh-waiting">
            {captureBlocked ? 'Needs SME' : part.status === 'in-progress' ? 'In progress' : 'Waiting'}
          </span>
        )}
      </div>
    </article>
  );
};

const UpNext: React.FC<{
  needsSme: boolean;
  smeNames: string[];
  nextPart: StreamMappingPart | null;
  completed: boolean;
}> = ({ needsSme, smeNames, nextPart, completed }) => {
  if (needsSme) {
    return (
      <aside className="smh-upnext" aria-label="Up next">
        <span className="smh-eyebrow">Up next</span>
        <h2>Assign an SME</h2>
        <p>Mapping needs someone who knows this stream. The parts stay visible, but capture waits for an SME.</p>
        <Link to="/streams" className="smh-primary" data-testid="stream-hub-primary">
          Assign an SME
          <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </aside>
    );
  }

  if (completed || !nextPart) {
    return (
      <aside className="smh-upnext" aria-label="Up next">
        <span className="smh-eyebrow">Up next</span>
        <h2>Mapping complete</h2>
        <p>The frozen baseline is ready for the value-proven board. Review any part before the next workshop.</p>
      </aside>
    );
  }

  return (
    <aside className="smh-upnext" aria-label="Up next">
      <span className="smh-eyebrow">Up next</span>
      <h2>{nextPart.actionLabel} {nextPart.shortLabel.toLowerCase()}</h2>
      <p>{nextPart.summary}</p>
      <p className="smh-who">
        In the room: {smeNames.length > 0 ? smeNames.join(', ') : 'Invite list open'}.
      </p>
      <ClipboardList size={18} aria-hidden="true" />
    </aside>
  );
};

export default StreamMappingHub;
